import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import Select from "react-select";
import { toast } from "react-toastify";
import { ArrowRight } from "lucide-react";

import CreateSurveyHero from "../../components/Ui/CreateSurveyHero";
import SurveyDescriptionEditor from "../../components/SurveyDescriptionEditor";
import {
  // ageLimitOptions,
  createImageAsset,
  genderOptions,
  getOptionLabel,
} from "./EditSurveyShared";
import { SurveyService } from "../../service/saveSurvey";
import { resolvePhotoUrl } from "../../utils/profilePhoto";

const DESCRIPTION_LIMIT = 500;
const getTodayDateValue = () => {
  const today = new Date();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${today.getFullYear()}-${month}-${day}`;
};

let cachedSectors = null;
let cachedCountries = null;
let sectorsRequest = null;
let countriesRequest = null;

const fetchSurveySectors = async () => {
  if (cachedSectors) {
    return cachedSectors;
  }

  if (!sectorsRequest) {
    const token = localStorage.getItem("token");

    sectorsRequest = axios
      .post(
        `${process.env.REACT_APP_API_URL}/api/survey/sectors`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      )
      .then((response) => {
        if (response.status === 200) {
          cachedSectors = response.data.data || [];
          return cachedSectors;
        }

        console.log(response.data);
        return [];
      })
      .catch((error) => {
        console.error("Error fetching sectors:", error);
        return [];
      })
      .finally(() => {
        sectorsRequest = null;
      });
  }

  return sectorsRequest;
};

const fetchSurveyCountries = async () => {
  if (cachedCountries) {
    return cachedCountries;
  }

  if (!countriesRequest) {
    const token = localStorage.getItem("token");

    countriesRequest = axios
      .post(
        `${process.env.REACT_APP_API_URL}/api/survey/countries`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      )
      .then((response) => {
        if (response.status === 200) {
          cachedCountries = response.data.data || [];
          return cachedCountries;
        }

        console.log(response.data);
        return [];
      })
      .catch((error) => {
        console.error("Error fetching countries:", error);
        return [];
      })
      .finally(() => {
        countriesRequest = null;
      });
  }

  return countriesRequest;
};

const EditSurveyStep1 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const editSurveyId = location.state?.previewData?.survey?.id || location.state?.editData?.id;
  const surveyFileInputRef = useRef(null);

  console.log("Location State =>", location.state);
  console.log("Edit Data ===============>", location.state?.editData);

  const [sector, setSector] = useState([]);
  const [industry, setIndustry] = useState([]);
  const [country, setCountry] = useState([]);
  const [states, setStates] = useState([]);
  const [surveyImage, setSurveyImage] = useState(null);
  const [surveyImageRemoved, setSurveyImageRemoved] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const [questionsCache, setQuestionsCache] = useState([]);
  const [hashtagCache, setHashtagCache] = useState([]);

  const getOptionLabels = (options, values) => {
    return options
      .filter((option) => values.includes(option.value))
      .map((option) => option.label)
      .join(",");
  };

  const [formData, setFormData] = useState({
    surveyTitle: "",
    surveyDescription: "",
    surveyMediaCover: "",
    surveyStartDate: "",
    surveyEndDate: "",
    surveyTargetResponses: "",
    gender: [],
    // ageLimit: [],
    minAge: "",
    maxAge: "",
    sector: [],
    industry: [],
    country: [],
    state: [],
  });

  const sectorOptions = sector.map((item) => ({
    value: item.question_answer_id,
    label: item.answer,
  }));

  const industryOptions = industry.map((item) => ({
    value: item.question_answer_id,
    label: item.answer,
  }));

  const countryOptions = country.map((item) => ({
    value: item.id,
    label: item.country_name,
  }));

  const stateOptions = states.map((item) => ({
    value: item.state_id,
    label: item.state_name,
  }));

  const ageOptions = Array.from({ length: 83 }, (_, i) => ({
    value: i + 18,
    label: `${i + 18}`,
  }));

  const hasTargetAudience = Boolean(formData.sector || formData.industry || formData.country || formData.state);

  const isContinueDisabled = !formData.surveyTitle.trim();

  // const isContinueDisabled =
  //   !formData.surveyTitle.trim() ||
  //   isDescriptionEmpty ||
  //   !formData.surveyStartDate ||
  //   !formData.surveyEndDate ||
  //   !formData.surveyTargetResponses ||
  //   !formData.gender ||
  //   !formData.ageLimit;
  // !hasTargetAudience;

  useEffect(() => {
    let isActive = true;

    const loadInitialOptions = async () => {
      const [sectorList, countryList] = await Promise.all([fetchSurveySectors(), fetchSurveyCountries()]);

      if (!isActive) return;

      setSector(sectorList);
      setCountry(countryList);
    };

    loadInitialOptions();

    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    if (formData.sector.length > 0) {
      getIndustries(formData.sector);
    } else {
      setIndustry([]);
    }
  }, [formData.sector]);

  useEffect(() => {
    if (formData.country.length > 0) {
      getStates(formData.country);
    } else {
      setStates([]);
    }
  }, [formData.country]);

  const getIndustries = async (sectorId) => {
    try {
      const token = localStorage.getItem("token");

      const sectorIds = Array.isArray(sectorId) ? sectorId : [sectorId];

      let allIndustries = [];

      for (const id of sectorIds) {
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/survey/industries`,
          {
            sector_id: id,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );

        if (response.status === 200) {
          allIndustries = [...allIndustries, ...(response.data.data || [])];
        }
      }

      // if (response.status === 200) {
      //   setIndustry(response.data.data);
      // } else {
      //   console.log(response.data);
      // }

      // Duplicate remove
      const uniqueIndustries = allIndustries.filter((item, index, self) => index === self.findIndex((t) => t.question_answer_id === item.question_answer_id));

      setIndustry(uniqueIndustries);
    } catch (error) {
      console.error("Error fetching sectors:", error);
    }
  };

  // const getStates = async (countryId) => {
  //   console.log("Country IDs =>", countryId);
  //   try {
  //     const token = localStorage.getItem("token");

  //     let allStates = [];

  //     for (let id of countryId) {
  //       const response = await axios.post(
  //         `${process.env.REACT_APP_API_URL}/api/user/get-states`,
  //         {
  //           country_id: countryId,
  //         },
  //         {
  //           headers: {
  //             Authorization: `Bearer ${token}`,
  //             "Content-Type": "application/json",
  //           },
  //         }
  //       );

  //       if (response.status === 200) {
  //         allStates = [...allStates, ...response.data.data];
  //       }
  //     }

  //     // if (response.status === 200) {
  //     //   setStates(response.data.data);
  //     // } else {
  //     //   console.log(response.data);
  //     // }

  //   // Duplicate states remove
  //     setStates(
  //       [...new Map(allStates.map(item => [item.state_id, item])).values()]
  //     );

  //   } catch (error) {
  //     console.error("Error fetching sectors:", error);
  //   }
  // };

  const getStates = async (countryId) => {
    try {
      const token = localStorage.getItem("token");

      // console.log("Country IDs =>", countryId);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/user/get-states`,
        {
          country_id: countryId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.status === 200) {
        setStates(response.data.data || []);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSurveyFileChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    setSurveyImage(createImageAsset(file));
    setSurveyImageRemoved(false);
    e.target.value = "";
  };

  const handleSurveyDelete = () => {
    setSurveyImage(null);
    setSurveyImageRemoved(true);

    if (surveyFileInputRef.current) {
      surveyFileInputRef.current.value = "";
    }
  };

  const handleSurveyUploadClick = () => {
    surveyFileInputRef.current?.click();
  };

  const buildSurveySummary = () => ({
    surveyTitle: formData.surveyTitle,
    surveyDescription: formData.surveyDescription,
    surveyTargetResponses: formData.surveyTargetResponses,
    surveyStartDate: formData.surveyStartDate,
    surveyEndDate: formData.surveyEndDate,
    gender: formData.gender || "--",
    // ageRange: getOptionLabel(ageLimitOptions, formData.ageLimit),

    // ageRange: formData.ageLimit
    // .map((value) => getOptionLabel(ageLimitOptions, value))
    // .join(", "),

    ageRange: formData.minAge && formData.maxAge ? `${formData.minAge} - ${formData.maxAge}` : "--",

    // sector: getOptionLabel(sectorOptions, formData.sector),
    // industry: getOptionLabel(industryOptions, formData.industry),
    // country: getOptionLabel(countryOptions, formData.country),
    // state: getOptionLabel(stateOptions, formData.state),
    sector: formData.sector.map((id) => getOptionLabel(sectorOptions, id)).join(", "),
    industry: formData.industry.map((id) => getOptionLabel(industryOptions, id)).join(", "),
    country: formData.country.map((id) => getOptionLabel(countryOptions, id)).join(", "),
    state: formData.state.map((value) => getOptionLabel(stateOptions, value)).join(", "),
  });

  const buildSurveyPayload = () => {
    console.log("Country:", formData.country);
    console.log("State:", formData.state);

    const payload = {
      survey_title: formData.surveyTitle,
      survey_description: formData.surveyDescription,

      // age_groups:
      //   formData.minAge && formData.maxAge
      //     ? [`${formData.minAge}-${formData.maxAge}`]
      //     : "",

      min_age: Number(formData.minAge) || "",
      max_age: Number(formData.maxAge) || "",

      // gender: formData.gender.join(","),

      target_responses: formData.surveyTargetResponses ? Number(formData.surveyTargetResponses) : "",

      gender: formData.gender.length ? formData.gender.join(",") : "",

      sector: formData.sector.join(","),

      industry: formData.industry.join(","),

      country: formData.country.join(","),

      state: formData.state.join(","),

      // reward_per_participant: 20,

      // gender: formData.gender,
      // sector: formData.sector,
      // industry: formData.industry,
      // country: formData.country,
      // state: formData.state,

      // sector: formData.sector.join(","),
      // industry: formData.industry.join(","),

      // country: getOptionLabels(countryOptions, formData.country),
      // state: getOptionLabels(stateOptions, formData.state),

      // country: formData.country.join(","),
      // state: formData.state.join(","),  //ID CHIYE TO YE CODE LAGEGA

      // state: states
      //   .filter((item) => formData.state.includes(item.state_id))
      //   .map((item) => item.state_name)
      //   .join(","),
    };

    if (surveyImage?.file) payload.survey_image = surveyImage.file;
    else if (surveyImageRemoved) payload.survey_image = "";

    console.log("payload =================>:", payload);

    // Date tabhi bhejo jab value ho
    if (formData.surveyStartDate) {
      // payload.start_date = formData.surveyStartDate;
      payload.start_date = `${formData.surveyStartDate} 00:00:00`;
    }

    if (formData.surveyEndDate) {
      // payload.end_date = formData.surveyEndDate;
      payload.end_date = `${formData.surveyEndDate} 23:59:59`;
    }

    return payload;
  };

  const validateSurveyForm = () => {
    if (!formData.surveyTitle) {
      toast.error("Survey Title Required");
      return false;
    }

    if (!formData.minAge || !formData.maxAge) {
      toast.warn("Select both minimum and maximum age.");
      return false;
    }

    if (formData.minAge && formData.maxAge && Number(formData.minAge) > Number(formData.maxAge)) {
      toast.error("Minimum Age cannot be greater than Maximum Age.");
      return false;
    }

    if (formData.surveyEndDate && formData.surveyEndDate < getTodayDateValue()) {
      toast.error("End Date cannot be in the past.");
      return false;
    }

    if (formData.surveyStartDate && formData.surveyEndDate && formData.surveyEndDate < formData.surveyStartDate) {
      toast.error("End Date must be after Start Date.");
      return false;
    }

    // if (isDescriptionEmpty) {
    //   toast.error("Survey Description Required");
    //   return false;
    // }
    // if (!formData.surveyStartDate) {
    //   toast.error("Survey Start Date Required");
    //   return false;
    // }
    // if (!formData.surveyEndDate) {
    //   toast.error("Survey End Date Required");
    //   return false;
    // }
    // if (new Date(formData.surveyEndDate) < new Date(formData.surveyStartDate)) {
    //   toast.error("End Date must be greater than Start Date");
    //   return false;
    // }
    // if (!formData.surveyTargetResponses) {
    //   toast.error("Survey Target Responses Required");
    //   return false;
    // }
    // if (!formData.gender.length === 0) { //.length === 0
    //   toast.error("Survey Gender Required");
    //   return false;
    // }
    // if (!formData.ageLimit.length === 0) { //.length === 0
    //   toast.error("Survey Age Limit Required");
    //   return false;
    // }
    // if (!hasTargetAudience) {
    //   toast.error("At least one Target Audience field is required");
    //   return false;
    // }

    return true; // Returns true if all validations pass
  };

  const handleSaveAsDraft = async () => {
    if (!validateSurveyForm()) return;
    if (!editSurveyId) {
      toast.error("Survey ID is missing. Open the survey from My Surveys and try again.");
      return;
    }
    try {
      const response = await SurveyService.updateSurveyDetails(editSurveyId, buildSurveyPayload());

      if (response.status === 200 || response.status === 201) {
        toast.success("Survey saved as draft");
      }
    } catch (error) {
      console.error("Draft API Error =>", error);
      toast.error("Failed to save draft");
    }
  };

  const handleContinueToQuestion = async () => {
    if (!validateSurveyForm()) return;
    if (!editSurveyId) {
      toast.error("Survey ID is missing. Open the survey from My Surveys and try again.");
      return;
    }

    try {
      setIsLoading(true);

      const surveyId = editSurveyId;
      const response = await SurveyService.updateSurveyDetails(surveyId, buildSurveyPayload());

      if (response.status !== 200 && response.status !== 201) {
        toast.error("Failed to save survey");
        return;
      }

      navigate(`/edit-survey/step-2/${surveyId}`, {
        state: {
          surveySummary: buildSurveySummary(),
          formData,
          surveyImage,
          surveyImageRemoved,
          questions: questionsCache,
          hashtagItems: hashtagCache,
          isEdit: location.state?.isEdit || false,
          previewData: location.state?.previewData,
        },
      });
    } catch (error) {
      console.error("API Error =>", error);

      toast.error(error?.response?.data?.message || "Something went wrong.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!location.state?.isBack) return;

    if (location.state.formData) {
      setFormData((prev) => ({
        ...prev,
        ...location.state.formData,
      }));
    }

    setSurveyImage(location.state.surveyImage || null);
    setSurveyImageRemoved(Boolean(location.state.surveyImageRemoved));

    setQuestionsCache(location.state.questions || []);
    setHashtagCache(location.state.hashtagItems || []);

    navigate(location.pathname, {
      replace: true,
      state: {
        isEdit: location.state.isEdit,
        previewData: location.state.previewData,
        restoredFromBack: true,
      },
    });
  }, [location.state]);

  useEffect(() => {
    // if (!location.state?.isEdit || !location.state?.editData) return;
    // const survey = location.state.editData;

    if (!location.state?.isEdit || !location.state?.previewData || location.state?.isBack || location.state?.restoredFromBack) return;
    const survey = location.state.previewData.survey;
    const apiQuestions = location.state.previewData.questions || [];
    const apiTags = location.state.previewData.tags || [];

    setSurveyImage(survey.survey_image ? { preview: resolvePhotoUrl(survey.survey_image) } : null);
    setSurveyImageRemoved(false);

    setFormData((prev) => ({
      ...prev,
      surveyTitle: survey.survey_title || "",
      surveyDescription: survey.survey_description || "",
      surveyStartDate: survey.start_date ? survey.start_date.split("T")[0] : "",
      surveyEndDate: survey.end_date ? survey.end_date.split("T")[0] : "",
      surveyTargetResponses: survey.target_responses || "",

      // gender: survey.gender
      //   ? survey.gender.split(",").map(Number)
      //   : [],

      gender: survey.gender
        ? survey.gender
            .split(",")
            .map((g) => g.trim())
            .map((g) => genderOptions.find((x) => x.label.toLowerCase() === g.toLowerCase())?.value)
            .filter(Boolean)
        : [],

      sector: survey.sector_ids ? survey.sector_ids.split(",").map(Number) : [],

      industry: survey.industry_ids ? survey.industry_ids.split(",").map(Number) : [],

      country: survey.country_ids ? survey.country_ids.split(",").map(Number) : [],

      state: survey.state_ids ? survey.state_ids.split(",").map(Number) : [],

      minAge: survey.min_age || "",
      maxAge: survey.max_age || "",
    }));

    // setQuestionsCache(location.state.questions || []);
    const questionTypeMap = {
      single_select: "Single Select",
      multi_select: "Multi Select",
      rating: "Rating",
      open_end: "Open End",
    };

    setQuestionsCache(
      apiQuestions.map((q) => ({
        id: q.id,
        type: questionTypeMap[q.question_type],
        text: q.question_text,
        options:
          q.question_type === "open_end"
            ? []
            : q.options.map((o) => ({
                id: o.id,
                text: o.option_text,
              })),
      })),
    );

    setHashtagCache(apiTags.map((tag) => (tag.tag_name.startsWith("#") ? tag.tag_name : `#${tag.tag_name}`)));
  }, [location.state]);

  // useEffect(() => {
  //   if (!location.state?.isEdit) return;
  //   setQuestionsCache(location.state.questions || []);
  //   console.log("Questions Cache =>", location.state.questions);
  // }, []);

  return (
    <>
      {isLoading && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 bg-white bg-opacity-75 d-flex justify-content-center align-items-center"
          style={{ zIndex: 9999 }}
        >
          <div className="spinner-border text-danger" style={{ width: "3rem", height: "3rem" }} role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      <div className="dashboard-create-survey">
        <CreateSurveyHero currentStep={1} />

        <div className="row g-3">
          <div className="col-12">
            <div className="cs-card cs-form-card">
              <div className="cs-step-label">Step 1/3: Survey details</div>
              <div className="cs-progress-track">
                <div className="cs-progress-fill step-1"></div>
              </div>

              <div className="row g-3 customselectrow">
                <div className="col-12">
                  <label className="cs-label">
                    Survey Title<span className="cs-required">*</span>
                  </label>

                  <input
                    type="text"
                    id="surveyTitle"
                    name="surveyTitle"
                    className="cs-input"
                    placeholder="e.g Workplace AI adoption study"
                    value={formData.surveyTitle}
                    onChange={handleChange}
                  />

                  <div className="cs-count">{formData.surveyTitle.length}/100</div>
                </div>

                <div className="col-12">
                  <label className="cs-label">
                    Description
                    {/* <span className="cs-required">*</span> */}
                  </label>

                  <SurveyDescriptionEditor
                    value={formData.surveyDescription}
                    limit={DESCRIPTION_LIMIT}
                    onChange={(surveyDescription) => setFormData((prev) => ({ ...prev, surveyDescription }))}
                  />
                </div>

                <div className="col-12 col-md-12 mb-3">
                  <div className="survey-upload-wrapper">
                    <label className="survey-upload-label ft-14 inter graytext mb-2">
                      Survey Media <span>(Optional)</span>
                    </label>

                    <div className="survey-upload-box">
                      <input type="file" accept=".jpg,.jpeg,.png,.webp" ref={surveyFileInputRef} onChange={handleSurveyFileChange} hidden />

                      {!surveyImage ? (
                        <div className="upload-placeholder text-center" onClick={handleSurveyUploadClick}>
                          <div className="upload-icon">
                            <i className="far fa-file-image"></i>
                          </div>

                          <h6 className="ft-14 fw-semibold inter">
                            Drag & drop your cover image here or <span className="textred">Browse</span>
                          </h6>

                          <p className="ft-12 inter">Supported: JPG, PNG, WEBP (Max 5MB)</p>
                        </div>
                      ) : (
                        <div className="preview-container">
                          {/* {surveyImage && (
                            <>
                              <pre>{JSON.stringify(surveyImage, null, 2)}</pre>
                              <img src={surveyImage.preview} alt="" />
                            </>
                          )} */}

                          <img src={surveyImage.preview} alt="" className="preview-image" />

                          <div className="upload-actions text-center d-flex gap-3 justify-content-center py-3">
                            <button
                              type="button"
                              className="btn btn-primary w-33 fw-bold ft-14 text-white px-5 py-3 bgred rounded-2 cursor-target text-center hover:tw-bg-[#333]"
                              onClick={handleSurveyUploadClick}
                            >
                              Upload New
                            </button>

                            <button
                              type="button"
                              className="w-33 fw-bold ft-14 text-white px-5 py-3 bgred rounded-2 cursor-target text-center hover:tw-bg-[#333]"
                              onClick={handleSurveyDelete}
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="col-12 col-md-6">
                  <label className="cs-label">
                    Start Date
                    {/* <span className="cs-required">*</span> */}
                  </label>
                  <div className="cs-date-wrap">
                    <input
                      type="date"
                      id="surveyStartDate"
                      name="surveyStartDate"
                      className="cs-input"
                      placeholder="Start Date"
                      value={formData.surveyStartDate}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="col-12 col-md-6">
                  <label htmlFor="birthdate" className="cs-label">
                    End Date
                    {/* <span className="cs-required">*</span> */}
                  </label>

                  <div className="cs-date-wrap">
                    <input
                      type="date"
                      id="surveyEndDate"
                      name="surveyEndDate"
                      className="cs-input"
                      placeholder="End Date"
                      value={formData.surveyEndDate}
                      min={getTodayDateValue()}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="col-12 col-md-6">
                  <label className="cs-label">
                    Target Responses
                    {/* <span className="cs-required">*</span> */}
                  </label>
                  <input
                    type="number"
                    name="surveyTargetResponses"
                    value={formData.surveyTargetResponses}
                    onChange={handleChange}
                    placeholder="0"
                    className="cs-input"
                  />
                  <div className="cs-helper">Total Number of responses you want</div>
                </div>

                <div className="col-12 col-md-6">
                  <label className="cs-label">
                    Gender
                    {/* <span className="cs-required">*</span> */}
                  </label>
                  <div className="cs-select-wrap">
                    {/* <select                    
                      className="cs-select"
                      name="gender"
                      value={formData.gender}
                      onChange={handleChange}
                    >
                      <option value="">Select Gender</option>
                      {genderOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select> */}
                    <Select
                      isMulti
                      closeMenuOnSelect={false}
                      // hideSelectedOptions={false}
                      options={genderOptions}
                      value={genderOptions.filter((option) => formData.gender.includes(option.value))}
                      onChange={(selected) => {
                        setFormData((prev) => ({
                          ...prev,
                          gender: selected ? selected.map((item) => Number(item.value)) : [],
                        }));
                      }}
                      placeholder="Select Gender"
                      className="custom-react-select"
                      classNamePrefix="custom-react-select"
                    />
                    {/* <ChevronDown size={16} className="cs-field-icon" /> */}
                  </div>
                </div>

                {/* <div className="col-12 col-md-12">
                  <label className="cs-label">Age Limit
                    <span className="cs-required">*</span>
                    </label>
                  <div className="cs-select-wrap"> */}
                {/* <select                   
                      className="cs-select"
                      name="ageLimit"
                      value={formData.ageLimit}
                      onChange={handleChange}
                    >
                      <option value="">Select Age Range</option>
                      {ageLimitOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select> */}

                {/* <Select
                        isMulti
                        closeMenuOnSelect={false}
                        // hideSelectedOptions={false}
                        options={ageLimitOptions}
                        value={ageLimitOptions.filter((option) =>
                          formData.ageLimit.includes(option.value)
                        )}
                        onChange={(selected) => {
                          setFormData((prev) => ({
                            ...prev,
                            ageLimit: selected ? selected.map((item) => item.value) : [],
                          }));
                        }}
                        placeholder="Select Age Range"
                        className="custom-react-select"
                      /> */}

                {/* <ChevronDown size={16} className="cs-field-icon" /> */}
                {/* </div>
                </div> */}

                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="cs-label">
                      Minimum Age
                      {/* <span className="cs-required">*</span> */}
                    </label>

                    <Select
                      options={ageOptions}
                      value={ageOptions.find((item) => item.value === formData.minAge)}
                      onChange={(selected) =>
                        setFormData((prev) => ({
                          ...prev,
                          minAge: selected?.value || "",
                        }))
                      }
                      placeholder="Select Minimum Age"
                      className="custom-react-select"
                      classNamePrefix="custom-react-select"
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        menu: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="cs-label">
                      Maximum Age
                      {/* <span className="cs-required">*</span> */}
                    </label>

                    <Select
                      options={ageOptions}
                      value={ageOptions.find((item) => item.value === formData.maxAge)}
                      onChange={(selected) =>
                        setFormData((prev) => ({
                          ...prev,
                          maxAge: selected?.value || "",
                        }))
                      }
                      placeholder="Select Maximum Age"
                      className="custom-react-select"
                      classNamePrefix="custom-react-select"
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        menu: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                    />
                  </div>
                </div>

                <div className="col-12 cs-field-gap">
                  <div className="cs-audience-title">Target Audience</div>
                  <div className="cs-audience-subtitle">Select your target audience to get the most relevant responses.</div>
                  <div className="row g-2">
                    <div className="col-12 col-sm-6 col-lg-3">
                      {/* <Select                      
                        options={sectorOptions}
                        value={
                          sectorOptions.find(
                            (option) => option.value === formData.sector
                          ) || null
                        }
                        onChange={(selected) => {
                          setIndustry([]);

                          setFormData((prev) => ({
                            ...prev,
                            sector: selected?.value || "",
                            industry: "",
                          }));
                        }}
                        placeholder="Sector"
                        className="custom-react-select"
                      /> */}

                      <Select
                        isMulti
                        closeMenuOnSelect={false}
                        // hideSelectedOptions={false}
                        blurInputOnSelect={false}
                        options={sectorOptions}
                        value={sectorOptions.filter((option) => formData.sector.includes(option.value))}
                        onChange={(selected) => {
                          setIndustry([]);

                          setFormData((prev) => ({
                            ...prev,
                            sector: selected ? selected.map((item) => item.value) : [],
                            industry: [],
                          }));
                        }}
                        placeholder="Select Sector"
                        className="custom-react-select"
                        classNamePrefix="custom-react-select"
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                          menu: (base) => ({ ...base, zIndex: 9999 }),
                        }}
                      />
                    </div>

                    <div className="col-12 col-sm-6 col-lg-3">
                      {/* <Select
                        options={industryOptions}
                        value={
                          industryOptions.find(
                            (option) => option.value === formData.industry
                          ) || null
                        }
                        onChange={(selected) =>
                          handleChange({
                            target: {
                              name: "industry",
                              value: selected?.value || "",
                            },
                          })
                        }
                        placeholder="Industry"
                        className="custom-react-select"
                        isDisabled={!formData.sector}
                      /> */}
                      <Select
                        isMulti
                        closeMenuOnSelect={false}
                        // hideSelectedOptions={false}
                        blurInputOnSelect={false}
                        options={industryOptions}
                        value={industryOptions.filter((option) => formData.industry.includes(option.value))}
                        onChange={(selected) =>
                          setFormData((prev) => ({
                            ...prev,
                            industry: selected ? selected.map((item) => item.value) : [],
                          }))
                        }
                        placeholder="Select Industry"
                        className="custom-react-select"
                        classNamePrefix="custom-react-select"
                        isDisabled={formData.sector.length === 0}
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                          menu: (base) => ({ ...base, zIndex: 9999 }),
                        }}
                      />
                    </div>

                    <div className="col-12 col-sm-6 col-lg-3">
                      {/* <Select
                        options={countryOptions}
                        value={
                          countryOptions.find(
                            (option) => option.value === formData.country
                          ) || null
                        }
                        onChange={(selected) => {
                          setStates([]);

                          setFormData((prev) => ({
                            ...prev,
                            country: selected?.value || "",
                            state: "",
                          }));
                        }}
                        placeholder="Country"
                        className="custom-react-select"
                      /> */}

                      <Select
                        isMulti
                        closeMenuOnSelect={false}
                        // hideSelectedOptions={false}
                        blurInputOnSelect={false}
                        options={countryOptions}
                        value={countryOptions.filter((option) => formData.country.includes(option.value))}
                        onChange={(selected) => {
                          setStates([]);

                          setFormData((prev) => ({
                            ...prev,
                            country: selected ? selected.map((item) => item.value) : [],
                            state: [],
                          }));
                        }}
                        placeholder="Select Country"
                        className="custom-react-select"
                        classNamePrefix="custom-react-select"
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                          menu: (base) => ({ ...base, zIndex: 9999 }),
                        }}
                      />
                    </div>

                    <div className="col-12 col-sm-6 col-lg-3">
                      {/* <Select
                        options={stateOptions}
                        value={
                          stateOptions.find(
                            (option) => option.value === formData.state
                          ) || null
                        }
                        onChange={(selected) =>
                          handleChange({
                            target: {
                              name: "state",
                              value: selected?.value || "",
                            },
                          })
                        }
                        placeholder="States"
                        className="custom-react-select"
                        isDisabled={!formData.country}
                      /> */}

                      <Select
                        isMulti
                        closeMenuOnSelect={false}
                        // hideSelectedOptions={false}
                        blurInputOnSelect={false}
                        options={stateOptions}
                        value={stateOptions.filter((option) => formData.state.includes(option.value))}
                        onChange={(selected) =>
                          setFormData((prev) => ({
                            ...prev,
                            state: selected ? selected.map((item) => item.value) : [],
                          }))
                        }
                        placeholder="Select States"
                        className="custom-react-select"
                        classNamePrefix="custom-react-select"
                        isDisabled={formData.country.length === 0}
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                          menu: (base) => ({ ...base, zIndex: 9999 }),
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12">
            <div className="cs-card cs-actions-card">
              <div className="row g-4 justify-content-end">
                <div className="col-12 col-sm-auto">
                  <button type="button" className="cs-draft-btn w-100" onClick={handleSaveAsDraft}>
                    Save as Draft
                  </button>
                </div>
                <div className="col-12 col-sm-auto">
                  <button
                    type="button"
                    className="cs-primary-btn d-flex align-items-center justify-content-center gap-2 w-100"
                    onClick={handleContinueToQuestion}
                    disabled={isContinueDisabled}
                  >
                    <span>Continue to Question</span>
                    <ArrowRight size={15} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditSurveyStep1;
